import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Search, Trash2, Plus, Minus, ScanBarcode, User, Printer, X, Store, Receipt,
  Banknote, CreditCard, Smartphone, Wallet as WalletIcon, Coins, Lock, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { inr, fmtIST } from "@/lib/utils-format";
import { PosPaymentEditDialog } from "@/components/PosPaymentEditDialog";
import { QRCodeSVG } from "qrcode.react";

const PAY_METHODS = [
  { k: "cash", label: "Cash", icon: Banknote, color: "bg-emerald-500" },
  { k: "upi", label: "UPI", icon: Smartphone, color: "bg-purple-500" },
  { k: "gpay", label: "GPay", icon: Smartphone, color: "bg-blue-500" },
  { k: "phonepe", label: "PhonePe", icon: Smartphone, color: "bg-indigo-600" },
  { k: "card", label: "Card", icon: CreditCard, color: "bg-slate-700" },
  { k: "wallet", label: "Wallet", icon: WalletIcon, color: "bg-amber-500" },
  { k: "credit", label: "Credit", icon: Coins, color: "bg-red-500" },
];

export default function POSPage() {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role_id || user?.role) === "super_admin";
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(user?.branch_id || "");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [walkinName, setWalkinName] = useState("");
  const [walkinPhone, setWalkinPhone] = useState("");
  const [payments, setPayments] = useState([{ method: "cash", amount: 0, reference: "" }]);
  const [discount, setDiscount] = useState(0);
  const [redeemTierPts, setRedeemTierPts] = useState(0);
  const [discountAuth, setDiscountAuth] = useState(null);  // manager auth for current transaction
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [payEditOpen, setPayEditOpen] = useState(false);
  const [showCustomer, setShowCustomer] = useState(false);
  const searchRef = useRef(null);
  const printRef = useRef(null);

  // Discount permission (Phase C)
  const role = user?.role || user?.role_id;
  const posPerms = user?.permissions_effective?.pos || {};
  const canApplyDiscount = (
    ["super_admin", "admin", "branch_manager"].includes(role) ||
    posPerms.apply_discount === true ||
    (Array.isArray(posPerms) && posPerms.includes("apply_discount"))
  );
  const discountLocked = !canApplyDiscount && !discountAuth;

  useEffect(() => {
    api.get("/branches").then((r) => {
      setBranches(r.data);
      if (!branchId && r.data.length) {
        const first = user?.branch_id || (r.data.find((b) => b.type === "branch") || r.data[0]).id;
        setBranchId(first);
      }
    });
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/pos/products/search`, { params: { q, branch_id: branchId } });
        setResults(data);
      } catch (e) {}
    }, 180);
    return () => clearTimeout(t);
  }, [q, branchId]);

  const selectedCustomer = customers.find((c) => c.id === customerId);
  const loyaltyBalance = Number(selectedCustomer?.loyalty_points || 0);

  // Loyalty settings + tier selection for this sale
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  useEffect(() => {
    api.get("/loyalty/settings").then((r) => setLoyaltySettings(r.data)).catch(() => {});
  }, []);

  // Company default GST rate (for lines whose product lacks gst_rate) — used
  // for tax-INCLUSIVE reverse calculation from MRP.
  const [defaultGstRate, setDefaultGstRate] = useState(0);
  const [company, setCompany] = useState({});
  useEffect(() => {
    api.get("/settings").then((r) => { setDefaultGstRate(Number(r.data?.default_gst_rate || 0)); setCompany(r.data || {}); }).catch(() => {});
  }, []);
  useEffect(() => { setRedeemTierPts(0); }, [customerId]);
  const availableTiers = React.useMemo(() => {
    const tiers = (loyaltySettings?.redemption_tiers || []).slice().sort((a, b) => a.min_points - b.min_points);
    return tiers.filter((t) => loyaltyBalance >= Number(t.min_points));
  }, [loyaltySettings, loyaltyBalance]);
  const nextTier = React.useMemo(() => {
    const tiers = (loyaltySettings?.redemption_tiers || []).slice().sort((a, b) => a.min_points - b.min_points);
    return tiers.find((t) => loyaltyBalance < Number(t.min_points));
  }, [loyaltySettings, loyaltyBalance]);

  const addItem = (p) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === p.id);
      if (existing) {
        return prev.map((i) => i.product_id === p.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, {
        product_id: p.id, name: p.name, sku: p.sku,
        quantity: 1, unit: p.unit || "pcs",
        unit_price: Number(p.sale_price || 0),
        gst_rate: Number(p.gst_rate || 0),
        discount_pct: 0,
        branch_stock: p.branch_stock ?? null,
      }];
    });
    setQ("");
    setResults([]);
    searchRef.current?.focus();
  };

  const changeQty = (pid, delta) => {
    setItems((prev) => prev.map((i) => i.product_id === pid ? { ...i, quantity: Math.max(0.01, i.quantity + delta) } : i));
  };
  const setItemPrice = (pid, price) => {
    setItems((prev) => prev.map((i) => i.product_id === pid ? { ...i, unit_price: Math.max(0, Number(price) || 0) } : i));
  };
  const setItemDiscount = (pid, d) => {
    setItems((prev) => prev.map((i) => i.product_id === pid ? { ...i, discount_pct: Math.max(0, Math.min(100, Number(d) || 0)) } : i));
  };
  const removeItem = (pid) => setItems((prev) => prev.filter((i) => i.product_id !== pid));

  // Totals — POS math is GST-INCLUSIVE (unit_price = MRP inclusive of tax).
  // Reverse-calc: base = MRP / (1 + rate/100); gst = MRP - base.
  // Falls back to company default_gst_rate when a line has no gst_rate.
  const totals = useMemo(() => {
    // 1) Pre-discount MRP total (tax-inclusive)
    let grossIncl = 0;
    const lines = items.map((it) => {
      const gross = it.quantity * it.unit_price;                    // MRP × qty
      const netIncl = gross * (1 - (it.discount_pct || 0) / 100);   // line disc% applied
      grossIncl += netIncl;
      return { it, netIncl };
    });
    // 2) Footer discount + loyalty reduce the inclusive grand total
    const tier = (loyaltySettings?.redemption_tiers || []).find((t) => Number(t.min_points) === Number(redeemTierPts));
    const loyaltyDisc = tier ? round2(grossIncl * Number(tier.discount_pct) / 100) : 0;
    const extraDisc = Number(discount || 0);
    const grandFinal = Math.max(0, grossIncl - extraDisc - loyaltyDisc);
    const scale = grossIncl > 0 ? grandFinal / grossIncl : 0;
    // 3) Per-line reverse-calc on the DISCOUNTED inclusive amount
    let taxable = 0, gstTotal = 0;
    for (const { it, netIncl } of lines) {
      const rate = Number(it.gst_rate) || Number(defaultGstRate) || 0;
      const lineIncl = netIncl * scale;
      const lineTaxable = rate > 0 ? lineIncl / (1 + rate / 100) : lineIncl;
      taxable += lineTaxable;
      gstTotal += lineIncl - lineTaxable;
    }
    const gstR = round2(gstTotal);
    // 4) Force taxable = grandFinal - gst to avoid ±0.01 drift
    const taxableR = round2(grandFinal - gstR);
    const cgst = round2(gstR / 2);
    const sgst = round2(gstR - cgst);
    return {
      subtotal: taxableR,       // taxable value (reverse-calc from MRP)
      cgst, sgst,
      gst: gstR,
      grand: round2(grossIncl), // MRP grand (pre-discount)
      loyaltyDisc: round2(loyaltyDisc),
      grandFinal: round2(grandFinal),
    };
  }, [items, discount, redeemTierPts, loyaltySettings, defaultGstRate]);

  const paidAmount = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const remaining = round2(totals.grandFinal - paidAmount);
  const [cashTendered, setCashTendered] = useState("");
  const cashPaid = round2(payments.filter((p) => p.method === "cash").reduce((s, p) => s + Number(p.amount || 0), 0));
  const nonCashPaid = round2(paidAmount - cashPaid);
  const cashDue = Math.max(0, round2(totals.grandFinal - nonCashPaid));
  const tenderedNum = Number(cashTendered || 0);
  const hasCashLeg = payments.some((p) => p.method === "cash");
  const changeDue = tenderedNum > 0 && hasCashLeg ? Math.max(0, round2(tenderedNum - cashDue)) : 0;
  const cashShort = tenderedNum > 0 && hasCashLeg && cashDue > 0 && tenderedNum < cashDue - 0.009;
  const onCashTendered = (val) => {
    setCashTendered(val);
    const t = Number(val || 0);
    if (t <= 0) return;
    // the cash leg always carries whatever is still due after non-cash payments; the surplus is change
    setPayments((prev) => {
      const idx = prev.findIndex((p) => p.method === "cash");
      if (idx >= 0) return prev.map((p, i) => i === idx ? { ...p, amount: cashDue } : p);
      return [...prev.filter((p) => Number(p.amount) > 0), { method: "cash", amount: cashDue, reference: "" }];
    });
  };

  const addPayment = (m = "cash") => {
    setPayments((prev) => [...prev, { method: m, amount: Math.max(0, remaining), reference: "" }]);
  };
  const setPayment = (idx, patch) => setPayments((prev) => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  const removePayment = (idx) => setPayments((prev) => prev.filter((_, i) => i !== idx));

  // Focus search on mount + Ctrl+/ shortcut
  useEffect(() => {
    searchRef.current?.focus();
    const h = (e) => {
      if (e.key === "/" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const reset = () => {
    setItems([]);
    setPayments([{ method: "cash", amount: 0, reference: "" }]);
    setCashTendered("");
    setDiscount(0);
    setRedeemTierPts(0);
    setDiscountAuth(null);
    setNotes("");
    setCustomerId("");
    setWalkinName("");
    setWalkinPhone("");
    setBatchPreviews({});
    searchRef.current?.focus();
  };

  // FEFO batch previews per line — { product_id: { batches, uncovered_qty } }
  const [batchPreviews, setBatchPreviews] = useState({});
  useEffect(() => {
    if (!branchId || items.length === 0) { setBatchPreviews({}); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const next = {};
      await Promise.all(items.map(async (it) => {
        try {
          const res = await api.get(`/pos/batch-preview`, {
            params: { branch_id: branchId, product_id: it.product_id, quantity: it.quantity },
          });
          next[it.product_id] = res.data;
        } catch (_) { /* ignore */ }
      }));
      if (!cancelled) setBatchPreviews(next);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [branchId, items]);

  const doCheckout = async () => {
    if (!branchId) return toast.error("Select a branch");
    if (items.length === 0) return toast.error("Add items to cart");
    if (Math.abs(paidAmount - totals.grandFinal) > 0.01 && !payments.some((p) => p.method === "credit")) {
      return toast.error(`Payments ₹${paidAmount} don't match total ₹${totals.grandFinal}`);
    }
    if (cashShort) return toast.error(`Cash received ₹${tenderedNum} is less than the cash due ₹${cashDue}`);
    setBusy(true);
    try {
      const body = {
        branch_id: branchId,
        customer_id: customerId || null,
        walkin_name: !customerId ? walkinName || null : null,
        walkin_phone: !customerId ? walkinPhone || null : null,
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name,
          quantity: i.quantity, unit_price: i.unit_price,
          gst_rate: i.gst_rate, discount_pct: i.discount_pct,
        })),
        payments: payments.filter((p) => Number(p.amount || 0) > 0),
        discount_amount: Number(discount || 0),
        redeem_tier_pts: redeemTierPts || null,
        discount_authorized_by: discountAuth ? {
          authorizer_id: discountAuth.authorizer_id,
          authorizer_name: discountAuth.authorizer_name,
          authorized_at: discountAuth.authorized_at,
          reason: discountAuth.reason,
          token: discountAuth.token,
        } : null,
        notes,
        is_interstate: false,
        cash_tendered: tenderedNum > 0 && cashPaid > 0 ? tenderedNum : null,
      };
      const { data } = await api.post("/pos/sales", body);
      toast.success(`Sale ${data.number} completed · ₹${data.grand_total}`);
      setLastSale(data);
      setShowReceipt(true);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setBusy(false);
    }
  };

  const currentBranch = branches.find((b) => b.id === branchId);
  const canSwitchBranch = user?.role === "super_admin" || user?.role === "admin" || !user?.branch_id;

  const printReceipt = () => {
    if (!printRef.current) return;
    const w = window.open("", "_blank", "width=380,height=700");
    if (!w) return;
    w.document.write(`<html><head><title>${lastSale?.number}</title><style>
      @media print { @page { size: 80mm auto; margin: 4mm; } body { margin: 0; } }
      body { font-family: 'Courier New', monospace; font-size: 12px; width: 72mm; }
      h1,h2,h3,h4 { margin: 2px 0; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      td { padding: 2px 0; }
      .r { text-align: right; }
      .c { text-align: center; }
      .bold { font-weight: bold; }
      .gstin { font-size: 13px; letter-spacing: 0.5px; }
      .divider { border-top: 1px dashed #000; margin: 4px 0; }
      .pos-print-only { display: block !important; }
    </style></head><body>${printRef.current.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  };

  return (
    <div className="-mx-6 -my-6 xl:h-[calc(100vh-4rem)] grid grid-cols-1 xl:grid-cols-[1fr_400px] bg-[#FDFBF7]" data-testid="pos-page">
      {/* LEFT — search + items */}
      <div className="flex flex-col min-h-0">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#EAE5D9] bg-white/60 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="uc-label">Fast billing</div>
            <h1 className="font-[Outfit] text-2xl font-semibold tracking-tight mt-0.5">POS Counter</h1>
          </div>
          <div className="flex items-center gap-2">
            <Store className="w-4 h-4 text-[#8B7355]" />
            {canSwitchBranch ? (
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger data-testid="pos-branch-select" className="h-9 w-52 bg-white"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Badge variant="outline" className="h-9 px-3">{currentBranch?.code} — {currentBranch?.name}</Badge>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-4">
          <div className="relative">
            <ScanBarcode className="w-5 h-5 text-[#8B7355] absolute left-3 top-3.5" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Scan barcode or search by name / SKU… (Ctrl+/)"
              className="w-full h-12 pl-11 pr-4 bg-white border-2 border-[#EAE5D9] focus:border-[#C05621] rounded-md text-base outline-none"
              data-testid="pos-search-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (results.length === 1) { addItem(results[0]); }
                  else if (results.length > 0) { addItem(results[0]); }
                }
                if (e.key === "Escape") { setQ(""); setResults([]); }
              }}
            />
            {results.length > 0 && (
              <div className="absolute z-30 mt-1 w-full bg-white border border-[#EAE5D9] rounded-md shadow-xl max-h-80 overflow-y-auto" data-testid="pos-search-results">
                {results.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => addItem(p)}
                    data-testid={`pos-result-${p.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-[#FEF9EA] flex items-center justify-between border-b border-[#EAE5D9] last:border-0"
                  >
                    <div>
                      <div className="font-medium text-sm">{p.name}</div>
                      <div className="text-xs text-[#8B7355]">SKU {p.sku} · GST {p.gst_rate}% · Stock {p.branch_stock ?? "—"}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-[#C05621]">₹{p.sale_price}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cart */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-[#8B7355]">
              <ScanBarcode className="w-16 h-16 mb-3 opacity-30" />
              <div className="text-lg font-[Outfit]">Scan a barcode or search a product</div>
              <div className="text-xs mt-1">Tip: Use a USB barcode scanner — it acts as a keyboard.</div>
            </div>
          ) : (
            <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F7F2E8] uc-label">
                  <tr>
                    <th className="text-left px-4 py-2.5">Item</th>
                    <th className="text-center px-2 py-2.5 w-40">Qty</th>
                    <th className="text-right px-2 py-2.5 w-28">Rate</th>
                    <th className="text-center px-2 py-2.5 w-20">Disc %</th>
                    <th className="text-right px-2 py-2.5 w-28">Amount</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const gross = it.quantity * it.unit_price;
                    const net = gross * (1 - it.discount_pct / 100);
                    return (
                      <tr key={it.product_id} className="border-t border-[#EAE5D9]" data-testid={`pos-cart-${it.product_id}`}>
                        <td className="px-4 py-2">
                          <div className="font-medium">{it.name}</div>
                          <div className="text-[10px] text-[#8B7355]">SKU {it.sku} · GST {it.gst_rate}%{typeof it.branch_stock === "number" && ` · Stock ${it.branch_stock}`}</div>
                          {(batchPreviews[it.product_id]?.batches || []).length > 0 && (
                            <div className="text-[10px] text-emerald-700 mt-0.5" data-testid={`pos-cart-batches-${it.product_id}`}>
                              FEFO: {batchPreviews[it.product_id].batches.map((a) => `${a.batch_no} (exp ${a.expiry_date || "N/A"}) ×${a.quantity}`).join(" · ")}
                              {batchPreviews[it.product_id].uncovered_qty > 0 && (
                                <span className="text-amber-600"> · {batchPreviews[it.product_id].uncovered_qty} unbatched</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-1 justify-center">
                            <button onClick={() => changeQty(it.product_id, -1)} data-testid={`pos-qty-dec-${it.product_id}`} className="w-7 h-7 rounded border border-[#EAE5D9] hover:border-[#C05621] flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                            <input
                              type="number" min="0.01" step="0.5"
                              value={it.quantity}
                              onChange={(e) => setItems((prev) => prev.map((x) => x.product_id === it.product_id ? { ...x, quantity: Math.max(0.01, Number(e.target.value) || 0) } : x))}
                              className="w-14 h-7 text-center border border-[#EAE5D9] rounded"
                            />
                            <button onClick={() => changeQty(it.product_id, +1)} data-testid={`pos-qty-inc-${it.product_id}`} className="w-7 h-7 rounded border border-[#EAE5D9] hover:border-[#C05621] flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" min="0" value={it.unit_price} onChange={(e) => setItemPrice(it.product_id, e.target.value)} className="w-full h-8 text-right border border-[#EAE5D9] rounded px-1" />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number" min="0" max="100" value={it.discount_pct}
                            onChange={(e) => {
                              if (discountLocked && Number(e.target.value) > 0) { setShowAuthDialog(true); return; }
                              setItemDiscount(it.product_id, e.target.value);
                            }}
                            disabled={discountLocked && it.discount_pct === 0}
                            className={`w-full h-8 text-center border rounded ${discountLocked && it.discount_pct === 0 ? 'bg-slate-100 border-slate-200 cursor-not-allowed' : 'border-[#EAE5D9]'}`}
                            title={discountLocked ? "Discounts require Store Manager approval" : ""}
                          />
                        </td>
                        <td className="px-2 py-2 text-right font-semibold">₹{round2(net).toLocaleString("en-IN")}</td>
                        <td className="px-2 py-2 text-right">
                          <button onClick={() => removeItem(it.product_id)} data-testid={`pos-rm-${it.product_id}`} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — Customer + Totals + Payments */}
      <aside className="border-t xl:border-t-0 xl:border-l border-[#EAE5D9] bg-white flex flex-col min-h-0 xl:overflow-y-auto">
        {/* Customer */}
        <div className="px-5 py-4 border-b border-[#EAE5D9]">
          <div className="flex items-center justify-between mb-2">
            <div className="uc-label">Customer</div>
            <button onClick={() => setShowCustomer(true)} data-testid="pos-customer-pick" className="text-xs text-[#C05621] hover:underline flex items-center gap-1">
              <User className="w-3 h-3" /> {customerId ? "Change" : "Choose"}
            </button>
          </div>
          {selectedCustomer ? (
            <div>
              <div className="font-semibold">{selectedCustomer.name}</div>
              <div className="text-xs text-[#8B7355]">{selectedCustomer.phone || selectedCustomer.email || "—"}</div>
              {loyaltyBalance > 0 && (
                <div className="mt-1 text-xs text-[#C05621]">Loyalty balance: {loyaltyBalance} pts</div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <input placeholder="Walk-in name (optional)" value={walkinName} onChange={(e) => setWalkinName(e.target.value)} data-testid="pos-walkin-name" className="w-full h-9 rounded-md border border-[#EAE5D9] px-2 text-sm outline-none focus:border-[#C05621]" />
              <input placeholder="Phone (optional — for WhatsApp invoice)" value={walkinPhone} onChange={(e) => setWalkinPhone(e.target.value)} data-testid="pos-walkin-phone" className="w-full h-9 rounded-md border border-[#EAE5D9] px-2 text-sm outline-none focus:border-[#C05621]" />
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="px-5 py-4 border-b border-[#EAE5D9]">
          <div className="uc-label mb-2">Bill summary</div>
          <div className="space-y-1.5 text-sm">
            <Row label="Taxable value" value={inr(totals.subtotal)} />
            <Row label={`GST (CGST ₹${totals.cgst} + SGST ₹${totals.sgst})`} value={inr(totals.gst)} />
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#5C4033] flex-1">Extra discount</span>
              {discountLocked ? (
                <button
                  type="button"
                  onClick={() => setShowAuthDialog(true)}
                  data-testid="pos-discount-unlock-btn"
                  className="flex items-center gap-1 h-8 px-3 text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded hover:bg-amber-100 font-medium"
                >
                  <Lock className="w-3 h-3" /> Request Manager Discount
                </button>
              ) : (
                <input
                  type="number" min="0" value={discount}
                  onChange={(e) => setDiscount(Math.max(0, Number(e.target.value) || 0))}
                  data-testid="pos-discount-input"
                  className="w-24 h-8 text-right border border-[#EAE5D9] rounded px-1"
                />
              )}
            </div>
            {discountAuth && (
              <div className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-center justify-between" data-testid="pos-discount-auth-banner">
                <span>✓ Discounts unlocked by <b>{discountAuth.authorizer_name}</b></span>
                <button onClick={() => { setDiscountAuth(null); setDiscount(0); }} className="text-red-600 hover:underline">revoke</button>
              </div>
            )}
            {selectedCustomer && (availableTiers.length > 0 || nextTier) && (
              <div className="rounded-md bg-[#FAF6EE] border border-[#EAE5D9] p-2 space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider text-[#8B7355] flex items-center justify-between">
                  <span>Redeem loyalty · {loyaltyBalance} pts available</span>
                  {redeemTierPts > 0 && (
                    <button onClick={() => setRedeemTierPts(0)} className="text-red-600 hover:underline" data-testid="pos-redeem-clear">clear</button>
                  )}
                </div>
                {availableTiers.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5" data-testid="pos-loyalty-tiers">
                    {availableTiers.map((t) => (
                      <button
                        key={t.min_points}
                        type="button"
                        onClick={() => setRedeemTierPts(Number(t.min_points))}
                        data-testid={`pos-redeem-tier-${t.min_points}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition ${redeemTierPts === Number(t.min_points)
                          ? "bg-[#C05621] text-white border-[#C05621]"
                          : "bg-white text-[#5C4033] border-[#EAE5D9] hover:border-[#C05621]"}`}
                      >
                        Use {t.min_points} pts · {t.label || `${t.discount_pct}% off`}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-[#8B7355]">
                    {nextTier ? `${Number(nextTier.min_points) - loyaltyBalance} more pts to unlock ${nextTier.label || `${nextTier.discount_pct}% off`}` : "No redemption tiers configured"}
                  </div>
                )}
                {redeemTierPts > 0 && totals.loyaltyDisc > 0 && (
                  <div className="flex items-baseline justify-between text-xs text-emerald-700">
                    <span>Loyalty discount applied</span>
                    <span className="font-semibold">- ₹{totals.loyaltyDisc.toLocaleString("en-IN")}</span>
                  </div>
                )}
              </div>
            )}
            <div className="border-t border-[#EAE5D9] pt-2 mt-2 flex items-baseline justify-between">
              <span className="text-sm font-semibold">Grand total</span>
              <span className="font-[Outfit] text-2xl font-bold text-[#C05621]" data-testid="pos-grand-total">₹{totals.grandFinal.toLocaleString("en-IN")}</span>
            </div>
          </div>
        </div>

        {/* Payments */}
        <div className="px-5 py-4 flex-1">
          <div className="uc-label mb-2 flex items-center justify-between">
            <span>Payment ({payments.filter((p) => p.amount > 0).length}/{PAY_METHODS.length} methods)</span>
            {Math.abs(remaining) > 0.01 && (
              <span className={remaining > 0 ? "text-red-500" : "text-emerald-600"}>
                {remaining > 0 ? `Due ₹${Math.abs(remaining)}` : `Change ₹${Math.abs(remaining)}`}
              </span>
            )}
          </div>

          {/* Quick add buttons */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {PAY_METHODS.map((m) => (
              <button
                key={m.k}
                onClick={() => setPayments((prev) => {
                  const idx = prev.findIndex((p) => p.method === m.k);
                  if (idx >= 0) return prev.map((p, i) => i === idx ? { ...p, amount: Number(p.amount || 0) + Math.max(0, remaining) } : p);
                  return [...prev.filter((p) => Number(p.amount) > 0 || p.method !== "cash" || prev.length === 1), { method: m.k, amount: Math.max(0, remaining), reference: "" }];
                })}
                data-testid={`pos-pay-${m.k}`}
                className="flex flex-col items-center gap-1 py-2 rounded-md border border-[#EAE5D9] hover:border-[#C05621] text-xs"
              >
                <div className={`w-8 h-8 rounded-full ${m.color} text-white flex items-center justify-center`}>
                  <m.icon className="w-4 h-4" />
                </div>
                {m.label}
              </button>
            ))}
          </div>

          {/* Payment rows */}
          <div className="space-y-2">
            {payments.map((p, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-2" data-testid={`pos-payment-row-${idx}`}>
                <Select value={p.method} onValueChange={(v) => setPayment(idx, { method: v })}>
                  <SelectTrigger className="h-9 w-28 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {PAY_METHODS.map((m) => <SelectItem key={m.k} value={m.k}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <input
                  type="number" min="0"
                  value={p.amount}
                  onChange={(e) => setPayment(idx, { amount: Math.max(0, Number(e.target.value) || 0) })}
                  placeholder="Amount"
                  className="flex-1 min-w-[6rem] h-9 rounded-md border border-[#EAE5D9] px-2 text-right outline-none focus:border-[#C05621]"
                />
                <input
                  value={p.reference || ""}
                  onChange={(e) => setPayment(idx, { reference: e.target.value })}
                  placeholder="Ref"
                  className="w-20 h-9 rounded-md border border-[#EAE5D9] px-2 outline-none focus:border-[#C05621] text-xs"
                />
                {payments.length > 1 && (
                  <button onClick={() => removePayment(idx)} className="text-red-500"><X className="w-4 h-4" /></button>
                )}
              </div>
            ))}
            <button
              onClick={() => addPayment(remaining > 0 ? "cash" : "cash")}
              data-testid="pos-add-payment"
              className="text-xs text-[#C05621] hover:underline"
            >
              + Add split payment
            </button>
          </div>
        </div>

        {/* Cash received → change due + Charge (compact, in normal flow so it never covers the payment methods) */}
        <div className='shrink-0 sticky bottom-0 z-10 border-t border-[#EAE5D9] bg-[#FDFBF7] shadow-[0_-6px_16px_-12px_rgba(0,0,0,0.35)]'>
          <div className={`px-4 py-2 ${cashShort ? 'bg-red-50' : changeDue > 0 ? 'bg-emerald-50' : 'bg-white'}`} data-testid='pos-cash-tendered-box'>
            <div className='flex items-center gap-2'>
              <label htmlFor='pos-cash-received' className='text-xs font-semibold text-[#2C1810] whitespace-nowrap'>Cash Received</label>
              <input
                id='pos-cash-received'
                type='number' min='0' inputMode='decimal'
                value={cashTendered}
                onChange={(e) => onCashTendered(e.target.value)}
                placeholder='Amount from customer'
                data-testid='pos-cash-tendered'
                className='flex-1 min-w-0 h-8 rounded-md border border-[#EAE5D9] px-2 text-right text-sm font-semibold outline-none focus:border-[#C05621] bg-white'
              />
              <div className='text-right leading-tight shrink-0'>
                <div className='text-[10px] font-semibold uppercase text-[#8B7355]'>Change Due</div>
                <div className={`font-[Outfit] text-base font-bold tabular-nums ${cashShort ? 'text-red-600' : changeDue > 0 ? 'text-emerald-700' : 'text-[#8B7355]'}`} data-testid='pos-change-due'>
                  {cashShort ? `Short ₹${round2(cashDue - tenderedNum).toLocaleString('en-IN')}` : `₹${changeDue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                </div>
              </div>
            </div>
            <div className='flex gap-1 mt-1.5'>
              {[100, 200, 500, 2000].map((n) => (
                <button key={n} type='button' onClick={() => onCashTendered(String(n))} data-testid={`pos-tender-quick-${n}`}
                        className='flex-1 text-xs py-1 rounded border border-[#EAE5D9] bg-white hover:border-[#C05621] hover:bg-[#FDF3EC] font-medium'>₹{n}</button>
              ))}
              <button type='button' onClick={() => onCashTendered(String(cashDue))} disabled={cashDue <= 0} data-testid='pos-tender-exact'
                      className='flex-1 text-xs py-1 rounded border border-[#C05621] text-[#C05621] bg-white hover:bg-[#FDF3EC] font-semibold disabled:opacity-40'>Exact</button>
            </div>
            {cashShort && (
              <div className='mt-1 text-[11px] font-medium text-red-700' data-testid='pos-cash-short-warning'>
                Cash received is less than the cash due ₹{cashDue.toLocaleString('en-IN')} — collect the balance or add a split payment.
              </div>
            )}
          </div>

          {/* Checkout */}
          <div className='px-4 py-2 border-t border-[#EAE5D9]'>
            <div className='grid grid-cols-2 gap-2'>
              <Button variant='outline' onClick={reset} data-testid='pos-cancel-btn' className='h-10'>
                <X className='w-4 h-4 mr-1' /> Reset
              </Button>
              <Button
                onClick={doCheckout}
                disabled={busy || items.length === 0 || cashShort}
                data-testid='pos-checkout-btn'
                className='bg-[#C05621] hover:bg-[#9C4221] text-white font-semibold h-10'
              >
                <Receipt className='w-4 h-4 mr-1' />
                {busy ? 'Processing…' : `Charge ₹${totals.grandFinal.toLocaleString('en-IN')}`}
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Customer picker */}
      <Dialog open={showCustomer} onOpenChange={setShowCustomer}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">Choose customer</DialogTitle>
            <DialogDescription>Pick an existing customer to award loyalty & attach history.</DialogDescription>
          </DialogHeader>
          <input placeholder="Search…" className="w-full h-10 border border-[#EAE5D9] rounded-md px-3 mb-3 outline-none focus:border-[#C05621]" onChange={(e) => {
            const s = e.target.value.toLowerCase();
            document.querySelectorAll("[data-cust-row]").forEach((el) => {
              el.style.display = el.getAttribute("data-cust-row").toLowerCase().includes(s) ? "flex" : "none";
            });
          }} data-testid="pos-cust-search" />
          <div className="max-h-72 overflow-y-auto divide-y divide-[#EAE5D9]">
            <button onClick={() => { setCustomerId(""); setShowCustomer(false); }} className="w-full text-left py-2 hover:bg-[#FEF9EA] px-2" data-testid="pos-cust-walkin">
              <div className="font-medium">Walk-in customer</div>
              <div className="text-xs text-[#8B7355]">Anonymous — no loyalty</div>
            </button>
            {customers.map((c) => (
              <button
                key={c.id}
                data-cust-row={`${c.name} ${c.phone || ""} ${c.email || ""}`}
                onClick={() => { setCustomerId(c.id); setShowCustomer(false); }}
                data-testid={`pos-cust-${c.id}`}
                className="w-full text-left py-2 hover:bg-[#FEF9EA] px-2 flex items-center justify-between"
              >
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-[#8B7355]">{c.phone || c.email || "—"}</div>
                </div>
                {c.loyalty_points > 0 && <Badge className="bg-[#C05621] text-white">{c.loyalty_points} pts</Badge>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt */}
      <PosPaymentEditDialog sale={lastSale} open={payEditOpen} onClose={() => setPayEditOpen(false)} onSaved={(d) => setLastSale(d)} />
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] flex items-center gap-2"><Receipt className="w-5 h-5 text-[#C05621]" /> Sale complete</DialogTitle>
            <DialogDescription>Print an 80mm thermal receipt or share via WhatsApp.</DialogDescription>
          </DialogHeader>
          {lastSale && (
            <div ref={printRef} className="text-[12px] font-mono leading-tight text-black">
              <h3 className="c bold" style={{ textAlign: "center", fontWeight: 700 }} data-testid="pos-receipt-company">{(company.company_name || "Bakemill Foods Private Limited").toUpperCase()}</h3>
              {company.gstin && (
                <div className="c bold gstin" style={{ textAlign: "center", fontWeight: 700, fontSize: 13, letterSpacing: "0.5px", margin: "2px 0" }} data-testid="pos-receipt-gstin">
                  GSTIN: {company.gstin}
                </div>
              )}
              <div className="c" style={{ textAlign: "center" }}>{lastSale.branch_name} ({lastSale.branch_code})</div>
              <div className="divider" style={{ borderTop: "1px dashed #000", margin: "4px 0" }}></div>
              <div className="c bold" style={{ textAlign: "center", fontWeight: 700, fontSize: 13, textTransform: "uppercase", margin: "2px 0" }} data-testid="pos-receipt-title">Tax Invoice</div>
              <div className="divider" style={{ borderTop: "1px dashed #000", margin: "4px 0" }}></div>
              <div>Invoice No: <b data-testid="pos-receipt-number">{lastSale.number}</b></div>
              <div>Date: <span data-testid="pos-receipt-datetime">{fmtIST(lastSale.created_at)}</span></div>
              <div>Cust: {lastSale.customer_snapshot?.name || "Walk-in"}</div>
              <div className="divider" style={{ borderTop: "1px dashed #000", margin: "4px 0" }}></div>
              <table style={{ width: "100%", fontSize: 11 }}>
                <thead>
                  <tr><th style={{ textAlign: "left" }}>Item</th><th className="r" style={{ textAlign: "right" }}>Qty×₹</th><th className="r" style={{ textAlign: "right" }}>Amt</th></tr>
                </thead>
                <tbody>
                  {lastSale.items.map((it, i) => (
                    <React.Fragment key={i}>
                      <tr><td>{it.name}</td><td className="r" style={{ textAlign: "right" }}>{it.quantity}×{it.unit_price}</td><td className="r" style={{ textAlign: "right" }}>{round2(it.quantity * it.unit_price * (1 - (it.discount_pct||0)/100))}</td></tr>
                      {(it.batches || []).map((a, j) => (
                        <tr key={`b-${i}-${j}`}>
                          <td colSpan={3} style={{ fontSize: 10, paddingLeft: 8, color: "#555" }}>
                            ↳ Batch {a.batch_no || "-"} · exp {a.expiry_date || "N/A"} × {a.quantity}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              <div className="divider" style={{ borderTop: "1px dashed #000", margin: "4px 0" }}></div>
              <table style={{ width: "100%" }}>
                <tbody>
                  <tr><td>Subtotal</td><td className="r" style={{ textAlign: "right" }}>₹{lastSale.subtotal}</td></tr>
                  <tr><td>CGST</td><td className="r" style={{ textAlign: "right" }}>₹{lastSale.cgst}</td></tr>
                  <tr><td>SGST</td><td className="r" style={{ textAlign: "right" }}>₹{lastSale.sgst}</td></tr>
                  {lastSale.discount_amount > 0 && <tr><td>Discount</td><td className="r" style={{ textAlign: "right" }}>-₹{lastSale.discount_amount}</td></tr>}
                  {lastSale.loyalty_redeemed > 0 && <tr><td>Loyalty Redeemed</td><td className="r" style={{ textAlign: "right" }}>-₹{lastSale.loyalty_redeemed}</td></tr>}
                  <tr className="bold"><td style={{ fontWeight: 700 }}>TOTAL</td><td className="r" style={{ textAlign: "right", fontWeight: 700 }}>₹{lastSale.grand_total}</td></tr>
                </tbody>
              </table>
              <div className="divider" style={{ borderTop: "1px dashed #000", margin: "4px 0" }}></div>
              {lastSale.payments?.map((p, i) => (
                <div key={i}>{p.method.toUpperCase()}: ₹{p.amount} {p.reference && `(${p.reference})`}</div>
              ))}
              {(() => {
                const cashLeg = (lastSale.payments || []).filter((p) => p.method === "cash").reduce((s, p) => s + Number(p.amount || 0), 0);
                if (!(lastSale.cash_tendered > 0) && cashLeg <= 0) return null;
                const received = lastSale.cash_tendered > 0 ? Number(lastSale.cash_tendered) : cashLeg;
                const change = lastSale.cash_tendered > 0 ? Number(lastSale.change_due || 0) : 0;
                return (
                  <table style={{ width: "100%", marginTop: 2 }} data-testid="pos-receipt-change">
                    <tbody>
                      <tr><td>Cash Received</td><td className="r" style={{ textAlign: "right" }}>₹{received.toFixed(2)}</td></tr>
                      <tr className="bold"><td style={{ fontWeight: 700 }}>CHANGE DUE</td><td className="r" style={{ textAlign: "right", fontWeight: 700 }}>₹{change.toFixed(2)}</td></tr>
                    </tbody>
                  </table>
                );
              })()}
              {lastSale.loyalty_awarded > 0 && <div style={{ marginTop: 4 }}>★ Earned {lastSale.loyalty_awarded} loyalty pts (bal: {lastSale.loyalty_balance})</div>}
              <div className="divider" style={{ borderTop: "1px dashed #000", margin: "4px 0" }}></div>
              {(() => {
                const addr = [company.address, company.city, company.state, company.pincode].filter(Boolean).join(", ");
                return (addr || company.phone || company.fssai_license) ? (
                  <div className="c" style={{ textAlign: "center", fontSize: 11, lineHeight: 1.35, margin: "4px 0" }} data-testid="pos-receipt-footer">
                    {addr && <div data-testid="pos-receipt-address">{addr}</div>}
                    {company.phone && <div data-testid="pos-receipt-phone">Ph: {company.phone}</div>}
                    {company.fssai_license && <div className="bold" style={{ fontWeight: 700 }} data-testid="pos-receipt-fssai">FSSAI Lic. No: {company.fssai_license}</div>}
                  </div>
                ) : null;
              })()}
              {(() => {
                // Scan-to-pay UPI QR — printed bill only (hidden on screen via .pos-print-only)
                const amt = Number(lastSale.grand_total || 0);
                if (!company.upi_id || !(amt > 0) || lastSale.status === "voided") return null;
                const upi = `upi://pay?pa=${encodeURIComponent(company.upi_id)}${company.upi_payee_name ? `&pn=${encodeURIComponent(company.upi_payee_name)}` : ""}&am=${amt.toFixed(2)}&cu=INR&tn=${encodeURIComponent(`Bill ${lastSale.number || ""}`)}`;
                return (
                  <div className="pos-print-only c" style={{ textAlign: "center", margin: "6px 0" }} data-testid="pos-receipt-upi-qr">
                    <div className="bold" style={{ fontWeight: 700, fontSize: 11 }}>SCAN TO PAY ₹{amt.toFixed(2)}</div>
                    <QRCodeSVG value={upi} size={128} level="M" includeMargin={false} style={{ margin: "4px auto", display: "block" }} />
                    <div style={{ fontSize: 10 }}>{company.upi_id} · GPay / PhonePe / any UPI app</div>
                  </div>
                );
              })()}
              <div className="c" style={{ textAlign: "center" }}>Thank you! Visit again.</div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-2">
            <Button variant="outline" onClick={() => setShowReceipt(false)} data-testid="pos-receipt-close">Close</Button>
            {isSuperAdmin && (
              <Button
                variant="outline"
                onClick={async () => {
                  const reason = window.prompt("Void reason? (e.g. wrong item, customer changed mind)");
                  if (reason === null) return;
                  try {
                    await api.post(`/pos/sales/${lastSale.id}/void`, { reason });
                    toast.success(`Sale ${lastSale.number} voided`);
                    setShowReceipt(false);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Void failed");
                  }
                }}
                data-testid="pos-receipt-void"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                Void sale
              </Button>
            )}
            {isSuperAdmin && (
              <Button
                variant="outline"
                onClick={async () => {
                  const name = window.prompt("Customer name:", lastSale.customer_snapshot?.name || "");
                  if (name === null) return;
                  const phone = window.prompt("Customer phone:", lastSale.customer_snapshot?.phone || "");
                  if (phone === null) return;
                  try {
                    await api.patch(`/pos/sales/${lastSale.id}`, { customer_name: name, customer_phone: phone });
                    toast.success(`Sale ${lastSale.number} updated`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Edit failed");
                  }
                }}
                data-testid="pos-receipt-edit"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                Edit (Super Admin)
              </Button>
            )}
            {isSuperAdmin && (
              <Button variant="outline" onClick={() => setPayEditOpen(true)} data-testid="pos-receipt-edit-payment"
                      className="border-[#C05621] text-[#C05621] hover:bg-[#FDF3EC]">
                Edit payment method
              </Button>
            )}
            {lastSale?.customer_snapshot?.phone && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    await api.post(`/pos/sales/${lastSale.id}/whatsapp`, { phone: lastSale.customer_snapshot.phone });
                    toast.success("WhatsApp sent");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "WhatsApp send failed");
                  }
                }}
                data-testid="pos-receipt-whatsapp"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              >
                <Smartphone className="w-4 h-4 mr-1" /> Send WhatsApp
              </Button>
            )}
            <Button onClick={printReceipt} data-testid="pos-receipt-print" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              <Printer className="w-4 h-4 mr-1" /> Print receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showAuthDialog && (
        <ManagerApprovalDialog
          onClose={() => setShowAuthDialog(false)}
          onApproved={(auth) => {
            setDiscountAuth(auth);
            setShowAuthDialog(false);
            toast.success(`Discounts unlocked by ${auth.authorizer_name}`);
          }}
        />
      )}
    </div>
  );
}

function ManagerApprovalDialog({ onClose, onApproved }) {
  const [mode, setMode] = useState("password"); // "password" | "pin" — password is primary per spec
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reasonValid = reason.trim().length >= 3;

  const submitPin = async (finalPin) => {
    setError("");
    if (!reasonValid) { setError("Reason is required (min 3 characters)"); setPin(""); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/pos/authorize-discount-pin", { pin: finalPin, reason: reason.trim() });
      onApproved(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "PIN authorisation failed";
      setError(typeof msg === "string" ? msg : "PIN authorisation failed");
      toast.error(typeof msg === "string" ? msg : "PIN authorisation failed");
      setPin("");
    } finally { setBusy(false); }
  };

  const submitCredentials = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Manager email and password are required"); return; }
    if (!reasonValid) { setError("Reason is required (min 3 characters)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/pos/authorize-discount", { email, password, reason: reason.trim() });
      onApproved(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Authorisation failed";
      setError(typeof msg === "string" ? msg : "Authorisation failed");
      toast.error(typeof msg === "string" ? msg : "Authorisation failed");
    } finally { setBusy(false); }
  };

  const handleDigit = (d) => {
    if (busy) return;
    if (!reasonValid) {
      setError("Please enter a reason first (min 3 characters) before typing the PIN");
      return;
    }
    setError("");
    setPin((prev) => {
      if (prev.length >= 6) return prev;
      const next = prev + d;
      if (next.length === 6) { submitPin(next); }
      return next;
    });
  };

  const handleBackspace = () => {
    if (busy) return;
    setError("");
    setPin((prev) => prev.slice(0, -1));
  };

  const TabBtn = ({ value, label, testid }) => (
    <button
      type="button"
      onClick={() => { setMode(value); setError(""); }}
      data-testid={testid}
      className={`flex-1 h-9 text-xs font-medium tracking-wider uppercase transition-colors ${
        mode === value
          ? "bg-[#C05621] text-white shadow-inner"
          : "bg-[#FAF6EE] text-[#8B7355] hover:bg-[#F5EDE0]"
      }`}
    >
      {label}
    </button>
  );

  const ReasonInput = (
    <div>
      <label className="text-[10px] uppercase text-[#8B7355]">
        Reason <span className="text-red-600">*</span> <span className="text-[#8B7355] normal-case tracking-normal">(min 3 chars, recorded to audit log)</span>
      </label>
      <input
        type="text" value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. VIP customer / damaged packaging / expiry near"
        data-testid="pos-manager-reason"
        className={`w-full h-9 rounded-md border px-2 text-sm outline-none focus:border-[#C05621] ${
          reason && !reasonValid ? "border-red-400 bg-red-50" : "border-[#EAE5D9]"
        }`}
      />
    </div>
  );

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm" data-testid="pos-manager-auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-amber-700" /> Store Manager approval
          </DialogTitle>
          <DialogDescription className="text-[11px] text-[#8B7355]">
            Cashiers cannot apply discounts. A Store Manager must approve — this override unlocks the discount for THIS bill only and is recorded to the audit log with the manager's name and reason.
          </DialogDescription>
        </DialogHeader>

        {/* Segmented tabs — Password (primary) | PIN */}
        <div className="flex rounded-md overflow-hidden border border-[#EAE5D9] mb-3">
          <TabBtn value="password" label="Password" testid="pos-manager-tab-password" />
          <TabBtn value="pin" label="6-digit PIN" testid="pos-manager-tab-pin" />
        </div>

        {mode === "password" ? (
          <form onSubmit={submitCredentials} className="space-y-3">
            <div>
              <label className="text-[10px] uppercase text-[#8B7355]">Manager email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                autoFocus required data-testid="pos-manager-email"
                className="w-full h-9 rounded-md border border-[#EAE5D9] px-2 text-sm outline-none focus:border-[#C05621]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[#8B7355]">Manager password</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="pos-manager-password"
                className="w-full h-9 rounded-md border border-[#EAE5D9] px-2 text-sm outline-none focus:border-[#C05621]"
              />
            </div>
            {ReasonInput}
            {error && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5" data-testid="pos-manager-error">
                {error}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                type="submit"
                disabled={busy || !reasonValid}
                className="bg-[#C05621] text-white hover:bg-[#a04519] disabled:opacity-50"
                data-testid="pos-manager-approve-submit"
              >
                {busy ? "Authorising…" : "Authorise Discount"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3">
            {ReasonInput}
            {/* PIN dots */}
            <div className="flex items-center justify-center gap-2 py-2" data-testid="pos-pin-dots">
              {[0,1,2,3,4,5].map((i) => (
                <div
                  key={i}
                  className={`w-3.5 h-3.5 rounded-full border ${
                    pin.length > i
                      ? "bg-[#C05621] border-[#C05621]"
                      : "bg-white border-[#EAE5D9]"
                  }`}
                />
              ))}
            </div>
            {/* Numeric keypad */}
            <div className="grid grid-cols-3 gap-2">
              {[1,2,3,4,5,6,7,8,9].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => handleDigit(String(n))}
                  disabled={busy}
                  data-testid={`pos-pin-key-${n}`}
                  className="h-12 rounded-md border border-[#EAE5D9] bg-[#FDFBF7] text-lg font-semibold text-[#2C1810] hover:bg-[#F5EDE0] active:scale-95 transition disabled:opacity-40"
                >
                  {n}
                </button>
              ))}
              <div />
              <button
                type="button"
                onClick={() => handleDigit("0")}
                disabled={busy}
                data-testid="pos-pin-key-0"
                className="h-12 rounded-md border border-[#EAE5D9] bg-[#FDFBF7] text-lg font-semibold text-[#2C1810] hover:bg-[#F5EDE0] active:scale-95 transition disabled:opacity-40"
              >
                0
              </button>
              <button
                type="button"
                onClick={handleBackspace}
                disabled={busy || !pin.length}
                data-testid="pos-pin-backspace"
                className="h-12 rounded-md border border-[#EAE5D9] bg-white text-sm font-medium text-[#8B7355] hover:bg-[#F5EDE0] active:scale-95 transition disabled:opacity-40"
              >
                ⌫
              </button>
            </div>
            {error && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5" data-testid="pos-manager-error">
                {error}
              </div>
            )}
            {busy && <div className="text-xs text-[#8B7355] text-center">Verifying PIN…</div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-[#5C4033]">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
